/**
 * Iter59.p24 — WCAG 2.1 relative luminance + contrast helpers.
 *
 * Both the picker page and the pytest suite use these formulas. The
 * backend has an equivalent implementation in `theme_contrast.py` so
 * the two agree on which combos are AA-compliant.
 */

/**
 * Parse a color string into a {r,g,b} tuple in the 0..255 range.
 * Accepts: #RGB, #RGBA, #RRGGBB, #RRGGBBAA, rgb()/rgba() and named
 * shortcuts "white" / "black" (that's the extent of what the picker
 * emits). Returns null for anything we can't parse.
 */
export function parseColor(input) {
    if (!input || typeof input !== "string") return null;
    const s = input.trim().toLowerCase();
    if (s === "white") return { r: 255, g: 255, b: 255, a: 1 };
    if (s === "black") return { r: 0, g: 0, b: 0, a: 1 };
    if (s.startsWith("#")) {
        const hex = s.slice(1);
        if (hex.length === 3) {
            return {
                r: parseInt(hex[0] + hex[0], 16),
                g: parseInt(hex[1] + hex[1], 16),
                b: parseInt(hex[2] + hex[2], 16),
                a: 1,
            };
        }
        if (hex.length === 6) {
            return {
                r: parseInt(hex.slice(0, 2), 16),
                g: parseInt(hex.slice(2, 4), 16),
                b: parseInt(hex.slice(4, 6), 16),
                a: 1,
            };
        }
        if (hex.length === 8) {
            return {
                r: parseInt(hex.slice(0, 2), 16),
                g: parseInt(hex.slice(2, 4), 16),
                b: parseInt(hex.slice(4, 6), 16),
                a: parseInt(hex.slice(6, 8), 16) / 255,
            };
        }
        return null;
    }
    const m = s.match(/^rgba?\(([^)]+)\)$/);
    if (m) {
        const parts = m[1].split(",").map((p) => p.trim());
        if (parts.length < 3) return null;
        return {
            r: Number(parts[0]),
            g: Number(parts[1]),
            b: Number(parts[2]),
            a: parts.length > 3 ? Number(parts[3]) : 1,
        };
    }
    return null;
}

/** WCAG 2.1 relative luminance of an sRGB channel component. */
function _channel(c) {
    const cs = c / 255;
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
}

/** WCAG 2.1 relative luminance of an sRGB colour. */
export function relativeLuminance(rgb) {
    if (!rgb) return null;
    return (
        0.2126 * _channel(rgb.r) +
        0.7152 * _channel(rgb.g) +
        0.0722 * _channel(rgb.b)
    );
}

/**
 * WCAG contrast ratio between two colours (1..21). Bigger = more
 * contrast. AA passes at ≥ 4.5:1 for normal text, ≥ 3:1 for large.
 */
export function contrastRatio(fg, bg) {
    const f = parseColor(fg);
    const b = parseColor(bg);
    if (!f || !b) return null;
    const L1 = relativeLuminance(f);
    const L2 = relativeLuminance(b);
    const light = Math.max(L1, L2);
    const dark = Math.min(L1, L2);
    return (light + 0.05) / (dark + 0.05);
}

/**
 * Returns true if the pair passes WCAG AA at the given size.
 * `large` = true means the text is >= 18pt or >= 14pt bold.
 */
export function passesAA(fg, bg, { large = false } = {}) {
    const r = contrastRatio(fg, bg);
    if (r == null) return false;
    return r >= (large ? 3.0 : 4.5);
}

/** Convert a {r,g,b} back to a #RRGGBB string. */
export function toHex(rgb) {
    if (!rgb) return "#000000";
    const h = (n) => Math.max(0, Math.min(255, Math.round(n)))
        .toString(16).padStart(2, "0");
    return `#${h(rgb.r)}${h(rgb.g)}${h(rgb.b)}`;
}

/**
 * Suggest the nearest AA-compliant variant of `fg` on `bg` by
 * darkening (for light backgrounds) or lightening (for dark
 * backgrounds) in HSL until the ratio passes. Returns a #RRGGBB
 * string, or the original if we can't find one within 100 steps.
 */
export function suggestAaFix(fg, bg) {
    const fgRgb = parseColor(fg);
    const bgRgb = parseColor(bg);
    if (!fgRgb || !bgRgb) return fg;
    const bgLum = relativeLuminance(bgRgb);
    const darken = bgLum > 0.5;

    let { r, g, b } = fgRgb;
    for (let step = 0; step < 100; step++) {
        const rgbStr = `#${[r, g, b].map((v) =>
            Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")
        ).join("")}`;
        if (passesAA(rgbStr, toHex(bgRgb))) return rgbStr;
        // shift towards black (if bg is light) or white (if bg is dark)
        const target = darken ? 0 : 255;
        r = r + (target - r) * 0.05;
        g = g + (target - g) * 0.05;
        b = b + (target - b) * 0.05;
    }
    return fg;
}
